import { Box, Button, Flex, Text, Textarea } from '@rocket/ui';
import { useEffect, useRef, useState } from 'react';
import { LuCheck, LuSend } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { ChatRuleCreated, ChatSessionContext } from '../onboarding-api';
import { onboardingChat } from '../onboarding-api';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  rulesCreated?: Array<ChatRuleCreated>;
}

interface Props {
  playbookId: string;
  contractType: string;
  skippedRules: Array<string>;
  onRulesCreated: (rules: Array<ChatRuleCreated>) => void;
}

export function OnboardingChatBox({
  playbookId,
  contractType,
  skippedRules,
  onRulesCreated,
}: Props) {
  const { bbAxios } = useFtueHost();
  const [messages, setMessages] = useState<Array<ChatMessage>>(() => {
    if (skippedRules.length === 0) {
      return [];
    }
    const ruleList = skippedRules.map((r) => `• ${r}`).join('\n');
    return [
      {
        role: 'assistant',
        content: `I found ${skippedRules.length} rule${skippedRules.length !== 1 ? 's' : ''} that need a bit more information to configure:\n${ruleList}\n\nFor governing law or forum rules, let me know your preferred jurisdiction (e.g. "Delaware"). For term rules, specify the preferred duration.`,
      },
    ];
  });
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionContext, setSessionContext] = useState<ChatSessionContext>({
    skipped_rules: skippedRules,
  });
  const [chatHistory, setChatHistory] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isSending) {
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);

    try {
      const result = await onboardingChat(bbAxios, {
        playbookId,
        contractType,
        message: text,
        sessionContext,
        chatHistory,
      });

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        rulesCreated: result.rulesCreated.length > 0 ? result.rulesCreated : undefined,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setSessionContext(result.sessionContext);
      setChatHistory(result.chatHistory ?? []);

      if (result.rulesCreated.length > 0) {
        onRulesCreated(result.rulesCreated);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  if (skippedRules.length === 0) {
    return null;
  }

  return (
    <Flex.V gap="3">
      <Text fontSize="sm" fontWeight="semibold">
        Rules needing setup ({skippedRules.length})
      </Text>

      {/* Message history */}
      <Box
        ref={scrollRef}
        borderWidth="1px"
        borderRadius="md"
        borderColor="border"
        bg="bg.subtle"
        maxH="52"
        overflowY="auto"
        p="3"
      >
        <Flex.V gap="2">
          {messages.map((msg, i) => (
            <Box key={i}>
              <Box
                bg={msg.role === 'user' ? 'blue.subtle' : 'bg.surface'}
                borderRadius="md"
                px="3"
                py="2"
                maxW="90%"
                css={{ marginLeft: msg.role === 'user' ? 'auto' : undefined }}
              >
                <Text fontSize="xs" whiteSpace="pre-wrap">
                  {msg.content}
                </Text>
              </Box>
              {msg.rulesCreated && msg.rulesCreated.length > 0 && (
                <Flex.V gap="1" mt="1">
                  {msg.rulesCreated.map((r) => (
                    <Box
                      key={r.ruleId}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor="green.muted"
                      bg="green.subtle"
                      px="2"
                      py="2"
                    >
                      <Flex.H align="center" gap="1" mb="1">
                        <LuCheck size={12} color="var(--chakra-colors-green-fg)" />
                        <Text fontSize="xs" color="green.fg" fontWeight="600">
                          {r.name}
                        </Text>
                      </Flex.H>
                      {r.description && (
                        <Text fontSize="xs" color="fg.muted" mb="1">
                          {r.description}
                        </Text>
                      )}
                      {(r.sampleEdit?.before || r.sampleEdit?.after) && (
                        <Flex.V gap="1">
                          {r.sampleEdit?.before && (
                            <Box
                              borderWidth="1px"
                              borderRadius="sm"
                              borderColor="border"
                              bg="bg.surface"
                              px="2"
                              py="1"
                            >
                              <Text fontSize="2xs" color="fg.subtle" fontWeight="600">
                                Before
                              </Text>
                              <Text fontSize="xs">{r.sampleEdit.before}</Text>
                            </Box>
                          )}
                          <Box
                            borderWidth="1px"
                            borderRadius="sm"
                            borderColor="border"
                            bg="bg.surface"
                            px="2"
                            py="1"
                          >
                            <Text fontSize="2xs" color="fg.subtle" fontWeight="600">
                              After
                            </Text>
                            <Text fontSize="xs">{r.sampleEdit?.after ?? ''}</Text>
                          </Box>
                        </Flex.V>
                      )}
                    </Box>
                  ))}
                </Flex.V>
              )}
            </Box>
          ))}
          {isSending && (
            <Box bg="bg.surface" borderRadius="md" px="3" py="2" maxW="60%">
              <Text fontSize="xs" color="fg.muted">
                Thinking…
              </Text>
            </Box>
          )}
        </Flex.V>
      </Box>

      {/* Input */}
      <Flex.H gap="2" align="flex-end">
        <Box flex="1">
          <Textarea
            placeholder="e.g. Delaware for governing law"
            value={input}
            onChange={(e) => setInput((e.target as HTMLTextAreaElement).value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            rows={2}
          />
        </Box>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={() => void sendMessage()}
          disabled={!input.trim() || isSending}
          loading={isSending}
          aria-label="Send"
        >
          <LuSend size={14} />
        </Button>
      </Flex.H>
    </Flex.V>
  );
}
